import type { QueryClient } from '@tanstack/react-query';
import type {
  CompensationProposalStatus,
  ICompensationChangedPayload,
  ICompensationProposal,
} from './types';

// Cache keys + the pure merges of the compensation panel (flight-delay U3). The WS
// half (`CompensationChanged@v2` → `lib/ws/apply-event.ts`) and the write hooks both
// fold into the cached lists through these, so a push, a write echo and a redelivery
// all land on the same state.

export type CompensationScope = 'current' | 'history';

/** Every compensation list of a disruption (the WS merge + refresh walk this prefix). */
export function compensationRoot(disruptionUrn: string): readonly unknown[] {
  return ['disruptions', disruptionUrn, 'compensation'];
}

/** `current` = `?current=true` (each group's highest revision); `history` = every revision. */
export function compensationKey(disruptionUrn: string, scope: CompensationScope): readonly unknown[] {
  return [...compensationRoot(disruptionUrn), scope];
}

const DECIDED: ReadonlySet<CompensationProposalStatus> = new Set(['APPROVED', 'REJECTED', 'SUPERSEDED']);

export function isUndecided(status: CompensationProposalStatus): boolean {
  return status === 'PROPOSED' || status === 'EDITED';
}

const ms = (iso: string | undefined): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/** The proposal's newest known instant — proposed, last edit or decision. */
export function latestAt(p: ICompensationProposal): number {
  return Math.max(ms(p.proposedAt), ...p.edits.map((e) => ms(e.by.at)), ms(p.decidedAt));
}

/**
 * Fold one `CompensationChanged` payload into a cached list.
 *
 * Ordering (TDS §14.7 — revision, then `changedAt`): the proposal URN names ONE
 * revision; within it a push older than what the list holds, or one that would move a
 * decided proposal back to undecided, is stale and ignored. Otherwise the fields the
 * push carries are patched (status, amounts; a decision also stamps `decidedBy` /
 * `decidedAt` from `changedBy`).
 *
 * `@v2` (overbooking U3): a denied-boarding push (`basis: 'DENIED_BOARDING'`) also
 * carries `basis` and its `computed` keys (regime, distance, reduction, reason codes,
 * engine) — they are folded over the row's verdict, never dropped (the row keeps what
 * the push does not carry: the band, the amount, the jurisdiction). A delay push leaves
 * `basis` and `computed` exactly as they were (`@v1`).
 *
 * `complete` says whether the list now reflects the change in full. An edit (the push
 * has no reason), a supersede (the new revision is elsewhere) and a URN the list does
 * not hold (a new revision / group) are NOT complete — the caller re-reads once.
 */
export function mergeCompensationChange(
  list: ICompensationProposal[] | undefined,
  change: ICompensationChangedPayload,
): { list: ICompensationProposal[] | undefined; complete: boolean } {
  if (!Array.isArray(list)) return { list, complete: true };
  const i = list.findIndex((p) => p.urn === change.proposalUrn);
  const cur = i >= 0 ? list[i] : undefined;
  if (!cur) return { list, complete: false };

  const at = latestAt(cur);
  const changedAt = ms(change.changedAt);
  // A projection may omit a nested object — keep what the list holds rather than crash.
  const perPax = change.perPax ?? cur.perPax;
  const total = change.total ?? cur.total;
  const deniedBoarding = change.basis === 'DENIED_BOARDING';
  const carried = !deniedBoarding || (cur.basis === 'DENIED_BOARDING' && holds(cur.computed, change.computed));
  const reflected =
    carried &&
    cur.status === change.status &&
    cur.perPax.amount === perPax.amount &&
    cur.perPax.currency === perPax.currency &&
    changedAt <= at;
  const stale = changedAt < at || (DECIDED.has(cur.status) && !DECIDED.has(change.status));
  if (reflected || stale) return { list, complete: true };

  const by = change.changedBy;
  const decided = (change.status === 'APPROVED' || change.status === 'REJECTED') && by !== undefined;
  const next: ICompensationProposal = {
    ...cur,
    ...(deniedBoarding
      ? {
          basis: 'DENIED_BOARDING' as const,
          computed: { ...cur.computed, ...(change.computed ?? {}) } as ICompensationProposal['computed'],
        }
      : {}),
    status: change.status,
    perPax,
    total,
    ...(decided && by ? { decidedBy: by, decidedAt: change.changedAt } : {}),
  };
  const out = list.slice();
  out[i] = next;
  return { list: out, complete: decided };
}

/** Every key the push's `computed` carries already has that value on the row's verdict. */
function holds(computed: object | undefined, patch: object | undefined): boolean {
  if (!patch) return true;
  if (!computed) return false;
  const row = computed as Record<string, unknown>;
  return Object.entries(patch).every(([key, value]) => {
    const have = row[key];
    if (Array.isArray(value)) return Array.isArray(have) && have.length === value.length && value.every((v, k) => have[k] === v);
    return have === value;
  });
}

/**
 * Upsert whole proposals (an edit / decision answer) into a cached list by URN. In the
 * `current` list a higher revision replaces its group's row; an unknown group appends.
 */
export function upsertProposals(
  list: ICompensationProposal[] | undefined,
  proposals: ICompensationProposal[],
  scope: CompensationScope,
): ICompensationProposal[] | undefined {
  if (!Array.isArray(list)) return list;
  let out = list;
  for (const p of proposals) {
    const i = out.findIndex(
      (q) => q.urn === p.urn || (scope === 'current' && q.subCaseUrn === p.subCaseUrn && q.revision < p.revision),
    );
    out = i >= 0 ? out.map((q, k) => (k === i ? p : q)) : [...out, p];
  }
  return out;
}

/** Write answered proposals into every cached compensation list of the disruption. */
export function writeProposals(qc: QueryClient, disruptionUrn: string, proposals: ICompensationProposal[]): void {
  if (proposals.length === 0) return;
  for (const scope of ['current', 'history'] as const) {
    qc.setQueryData<ICompensationProposal[]>(compensationKey(disruptionUrn, scope), (list) =>
      upsertProposals(list, proposals, scope),
    );
  }
}

/**
 * One re-read per burst. A bulk decision fans out one push per proposal; each one that
 * the merge could not express in full asks for a refresh, and this coalesces them into
 * a single invalidate per window (the overview-refresh precedent in `apply-event.ts`).
 * Push-driven: nothing is scheduled while the socket is quiet — never a poll.
 */
export const COMPENSATION_REFRESH_WINDOW_MS = 600;
const pending = new Map<string, ReturnType<typeof setTimeout>>();

export function scheduleCompensationRefresh(qc: QueryClient, disruptionUrn: string): void {
  if (pending.has(disruptionUrn)) return;
  pending.set(
    disruptionUrn,
    setTimeout(() => {
      pending.delete(disruptionUrn);
      void qc.invalidateQueries({ queryKey: compensationRoot(disruptionUrn) });
    }, COMPENSATION_REFRESH_WINDOW_MS),
  );
}
